import { AccessTime, CalendarMonth, LocationOn, Repeat, Schedule } from "@mui/icons-material";
import { Box, Card, CardContent, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { selectCurrentCompany } from "../../../../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../../../../utils";
import { flownectColors } from "../../../../../const";
import { getFrequencyDescription } from "../../../../../utils/getFrequencyDescription";
import { InfoRow } from "../single-event/InfoRow";
import { EventJobModalHeader } from "./EventJobModalHeader";
import { WorkforceSection } from "./EventJobWorkforceSection";
import { getDurationString } from "../utils/EventJobModal.utils";

export const EventJobModal = ({ arg, softBorders = false }) => {
    const currentCompany = useAppSelector(selectCurrentCompany);
    const companyTimeZone = currentCompany?.timeZone || "UTC";
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    if (!arg?.event) return null;

    const {
        title,
        extendedProps: { event: jobEvent, workforce, coverageArea, jobId, color, quote },
    } = arg.event;

    const job = { workforce, id: jobId, quote, ...arg?.event?.extendedProps?.job };
    const baseColor = color || "#1976d2";
    const contentX = softBorders ? 1.25 : 2.5;
    const getDisplayText = (value) => {
        const text = String(value || "").trim();
        return /^service\s*#/i.test(text) ? "" : text;
    };
    const displayTitle = getDisplayText(title) || getDisplayText(job?.alias);

    const formatDateTime = (dateStr) =>
        dateStr ? formatDate(dateStr, companyTimeZone, "MMM dd, yyyy • h:mm a") : "—";

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: softBorders ? 2 : 3,
                overflow: "hidden",
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                width: "100%",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                containerType: "inline-size",
                border: "none",
            }}
        >
            <EventJobModalHeader
                title={title}
                jobId={jobId}
                job={job}
                eventUid={jobEvent?.uid}
                visitDate={jobEvent?.startDate ? formatDate(jobEvent.startDate, companyTimeZone, "MMM dd, yyyy") : ""}
                compact={softBorders}
            />

            <CardContent
                sx={{
                    px: contentX,
                    pt: softBorders ? 0.75 : 1,
                    pb: softBorders ? 1 : 2.5,
                    flex: 1,
                    overflowY: "auto",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                }}
            >
                <Stack spacing={softBorders ? 1 : 2}>
                    {displayTitle && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: flownectColors.gray[900],
                                fontWeight: 500,
                                fontSize: softBorders ? 13 : "0.92rem",
                                lineHeight: 1.35,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {displayTitle}
                        </Typography>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            width: "100%",
                            p: softBorders ? 0.25 : 2.5,
                            borderRadius: softBorders ? 0 : 2,
                            border: "none",
                            "@container (max-width: 520px)": { p: 0.5 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                width: "100%",
                                columnGap: softBorders ? 1.25 : 3,
                                rowGap: softBorders ? 1.25 : 2.5,
                                alignItems: "start",
                                "& > :nth-of-type(1)": { justifySelf: "start" },
                                "& > :nth-of-type(2)": { justifySelf: "center" },
                                "& > :nth-of-type(3)": { justifySelf: "center", transform: "translateX(24px)" },
                                "@container (max-width: 520px)": {
                                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                    columnGap: 0.5,
                                    justifyItems: "start",
                                    "& > :nth-of-type(3)": { transform: "none" },
                                },
                            }}
                        >
                            <InfoRow compact={softBorders} icon={CalendarMonth} label="Start Date" value={formatDateTime(jobEvent?.startDate)} />
                            {jobEvent?.endDate && (
                                <InfoRow compact={softBorders} icon={AccessTime} label="End Date" value={formatDateTime(jobEvent?.endDate)} />
                            )}
                            <InfoRow compact={softBorders} icon={Schedule} label="Duration" value={getDurationString(jobEvent?.startDate, jobEvent?.endDate)} />
                            {getFrequencyDescription(jobEvent) && (
                                <InfoRow compact={softBorders} icon={Repeat} label="Frequency" value={getFrequencyDescription(jobEvent)} />
                            )}
                        </Box>
                    </Paper>

                    {(coverageArea?.address || workforce?.length > 0) && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0, alignItems: "start", mt: "24px !important" }}>
                            {coverageArea?.address && (
                                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ px: softBorders ? 0 : 1.5, py: softBorders ? 0 : 1.5 }}>
                                    <LocationOn sx={{ fontSize: softBorders ? 16 : 16, color: baseColor, mt: 0.25 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: flownectColors.gray[800],
                                            fontWeight: 500,
                                            fontSize: softBorders ? 13 : "0.875rem",
                                            lineHeight: softBorders ? 1.35 : 1.4,
                                            "@container (max-width: 520px)": { fontSize: softBorders ? 12 : "0.68rem", lineHeight: 1.25 },
                                        }}
                                    >
                                        {coverageArea.address}
                                    </Typography>
                                </Stack>
                            )}

                            {workforce?.length > 0 && (
                                <Box sx={{ mt: coverageArea?.address ? "24px !important" : 0 }}>
                                    <WorkforceSection
                                        workforce={workforce}
                                        baseColor={baseColor}
                                        companyTimeZone={companyTimeZone}
                                        now={now}
                                        compact={softBorders}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};
